import { RequestStatus } from 'features/request/requestSlice';
import { useRequest } from 'features/request/useRequest';
import { useResetStoreData } from 'features/request/useResetStoreData';

import { useCallback, useEffect, useMemo } from 'react';
import { useDispatch } from 'react-redux';

import type { IVariablesChangeData } from '../WorkOrderUseCoefficient';
import {
    uerApplyWorkOrderChangePriceRatiosToObjects,
    uerApplyWorkOrderChangePriceRatiosToRootObject,
} from '../workOrderUseCoefficientQueries';

interface IApplyRatioToWorkOrderChange {
    checkedIds: Record<string, boolean>;
    variablesChangeData: IVariablesChangeData;
    closeHandler: () => void;
    updaterApplyRatio?: () => void;
    valueNote: Record<string, string>;
}

export const useApplyRatioToWorkOrderChange = (props: IApplyRatioToWorkOrderChange) => {
    const { checkedIds, valueNote, variablesChangeData, closeHandler, updaterApplyRatio } = props;

    const priceListRatioIds = useMemo(
        () =>
            Object.keys(checkedIds)
                .filter((key) => checkedIds[key])
                .map(Number)
                .map((id) => ({
                    rationale: valueNote[id] || '',
                    ratioRowId: id,
                })),
        [checkedIds, valueNote],
    );

    const requestId = variablesChangeData?.isRootItem
        ? 'uerApplyWorkOrderChangePriceRatiosToRootObject'
        : 'uerApplyWorkOrderChangePriceRatiosToObjects';

    const dispatch = useDispatch();

    const onSuccess = useCallback(() => {
        updaterApplyRatio?.();
        closeHandler();
    }, [closeHandler, updaterApplyRatio]);

    const [, statusRootObject, , toRootObjectRequest] = useRequest({
        requestId,
        query: uerApplyWorkOrderChangePriceRatiosToRootObject,
        withEffect: false,
        dependenciesUpdater: onSuccess,
    });

    const [, statusObject, , toObjectRequest] = useRequest({
        requestId,
        query: uerApplyWorkOrderChangePriceRatiosToObjects,
        withEffect: false,
        dependenciesUpdater: onSuccess,
    });

    const isLoadingApplyOrderChange = variablesChangeData?.isRootItem
        ? statusRootObject === RequestStatus.FETCHING
        : statusObject === RequestStatus.FETCHING;

    const addRatioToWorkOrderChangeHandler = useCallback(() => {
        if (variablesChangeData?.isRootItem) {
            dispatch(
                toRootObjectRequest({
                    rootObjectIds: variablesChangeData.rootObjectIds || {},
                    workOrderChangeId: variablesChangeData.workOrderChangeId,
                    constructionTypeId: variablesChangeData.constructionTypeId,
                    priceListRatioIds,
                }),
            );
        } else {
            dispatch(
                toObjectRequest({
                    objectIds: variablesChangeData?.objectIds || [],
                    workOrderChangeId: variablesChangeData.workOrderChangeId,
                    constructionTypeId: variablesChangeData.constructionTypeId,
                    priceListRatioIds,
                }),
            );
        }
    }, [
        dispatch,
        priceListRatioIds,
        toObjectRequest,
        toRootObjectRequest,
        variablesChangeData.constructionTypeId,
        variablesChangeData?.isRootItem,
        variablesChangeData?.objectIds,
        variablesChangeData.rootObjectIds,
        variablesChangeData.workOrderChangeId,
    ]);

    const [resetAddUerFromWorkOrder] = useResetStoreData({ requestId });

    useEffect(
        () => () => {
            resetAddUerFromWorkOrder(undefined);
        },
        [resetAddUerFromWorkOrder],
    );

    return {
        addRatioToWorkOrderChangeHandler,
        isLoadingApplyOrderChange,
    };
};
